import { useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { useLocation } from '@remix-run/react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounceFetcher } from 'remix-utils/use-debounce-fetcher'
import { useHydrated } from 'remix-utils/use-hydrated'

import { type ErrorMessageProps } from '#app/components/ui/error-message/error-message.tsx'
import { type GetOrderItem } from '#app/models/ecommerce-providers/sylius/shop/fetch/order-item/get-order-item.ts'
import { type GetProductVariant } from '#app/models/ecommerce-providers/sylius/shop/fetch/product-variant/get-product-variant.ts'
import {
	CART_FORM_REMOVE_ITEM,
	CART_FORM_SET_QUANTITY,
	CART_I18NEXT,
	CART_INPUTS,
	cartRemoveItemSchema,
	cartSetQuantitySchema,
} from '#app/routes/($lang).cart/route-config'
import { type App } from '#app/routes/_layout/component.route.tsx'
import { ROOT_INPUTS } from '#app/routes/_layout/route-config.ts'
import {
	useCartRemoveItemNotify,
	useCartSetQuantityNotify,
} from '#app/routes/_layout/use-cart-notify.tsx'
import { CART_ACTION_TYPES, useCart } from '#app/utils/client/cart-context.tsx'

type UseCartItemProps = {
	setApp: React.Dispatch<React.SetStateAction<App>>
	name: string
	orderItemId: number
	slug: string
	variantCode: string
}

function useCartItem(props: UseCartItemProps) {
	const { setApp, name, orderItemId, variantCode } = props

	const { cartState, cartDispatch } = useCart()
	const item = cartState.order.items?.find(
		({ id, variant }: GetOrderItem) =>
			id === orderItemId || variantCode === (variant as GetProductVariant).code,
	)

	if (typeof item === 'undefined') {
		throw Error('Cart item cannot be undefined')
	}

	const pending = cartState.pending.filter(
		({ code }) => code === (item.variant as GetProductVariant).code,
	)
	const actionAdd = pending.find(
		({ action }) => action === CART_ACTION_TYPES.ADD_PRODUCT_TO_CART,
	)
	const actionUpdate = pending.find(
		({ action }) => action === CART_ACTION_TYPES.UPDATE_ITEM_FROM_CART,
	)
	const actionRemove = pending.find(
		({ action }) => action === CART_ACTION_TYPES.REMOVE_ITEM_FROM_CART,
	)

	const actionInit = actionAdd && !orderItemId

	const [errorMessage] = React.useState<ErrorMessageProps | undefined>(
		undefined,
	)

	/**
	 * Optimistic
	 */
	const isHydrated = useHydrated()
	const location = useLocation()

	/**
	 * Fetcher
	 */
	const setQuantityFetcherKey = CART_FORM_SET_QUANTITY.generateFetcherKey!({
		id: orderItemId?.toString(),
	})
	const setQuantityFetcher = useDebounceFetcher()

	const removeItemFetcherKey = CART_FORM_REMOVE_ITEM.generateFetcherKey!({
		id: orderItemId?.toString(),
	})
	const removeItemFetcher = useDebounceFetcher()

	/**
	 * Forms
	 */
	const setQuantityFormValues = {
		[ROOT_INPUTS.subaction]: CART_FORM_SET_QUANTITY.subaction,
		[ROOT_INPUTS.noJs]: !isHydrated.toString(),
		[ROOT_INPUTS.redirect]: `${location.pathname}`,
		[CART_INPUTS.orderItemId]: orderItemId?.toString(),
	}
	const [setQuantityForm, setQuantityFields] = useForm({
		defaultValue: {
			[CART_INPUTS.quantity]: item?.quantity,
		},
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: cartSetQuantitySchema })
		},
		shouldValidate: 'onSubmit',
		async onSubmit(e) {
			e.preventDefault()

			setQuantityFetcher.submit(e.currentTarget, {
				fetcherKey: setQuantityFetcherKey,
				debounceTimeout: 0,
			})

			cartDispatch({
				type: CART_ACTION_TYPES.UPDATE_ITEM_FROM_CART,
				payload: {
					quantity: item?.quantity!,
					id: orderItemId,
				},
			})
		},
	})
	const increaseQuantityFormValues = {
		[ROOT_INPUTS.subaction]: CART_FORM_SET_QUANTITY.subaction,
		[ROOT_INPUTS.noJs]: !isHydrated.toString(),
		[ROOT_INPUTS.redirect]: `${location.pathname}`,
		[CART_INPUTS.quantity]: item?.quantity! + 1,
		[CART_INPUTS.orderItemId]: orderItemId?.toString(),
	}
	const [increaseQuantityForm, increaseQuantityFields] = useForm({
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: cartSetQuantitySchema })
		},
		shouldValidate: 'onSubmit',
		async onSubmit(e) {
			e.preventDefault()

			setQuantityFetcher.submit(e.currentTarget, {
				fetcherKey: setQuantityFetcherKey,
				debounceTimeout: 0,
			})

			setQuantityForm.update({
				name: CART_INPUTS.quantity,
				value: Number(increaseQuantityForm.value!.quantity),
			})

			cartDispatch({
				type: CART_ACTION_TYPES.UPDATE_ITEM_FROM_CART,
				payload: {
					quantity: Number(increaseQuantityForm.value!.quantity),
					id: orderItemId,
				},
			})
		},
	})
	const decreaseQuantityFormValues = {
		[ROOT_INPUTS.subaction]: CART_FORM_SET_QUANTITY.subaction,
		[ROOT_INPUTS.noJs]: String(!isHydrated),
		[ROOT_INPUTS.redirect]: `${location.pathname}`,
		[CART_INPUTS.quantity]: Math.max(0, item?.quantity! - 1),
		[CART_INPUTS.orderItemId]: orderItemId?.toString(),
	}
	const [decreaseQuantityForm, decreaseQuantityFields] = useForm({
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: cartSetQuantitySchema })
		},
		shouldValidate: 'onSubmit',
		async onSubmit(e) {
			e.preventDefault()

			setQuantityFetcher.submit(e.currentTarget, {
				fetcherKey: setQuantityFetcherKey,
				debounceTimeout: 0,
			})

			setQuantityForm.update({
				name: CART_INPUTS.quantity,
				value: Number(decreaseQuantityForm.value!.quantity),
			})

			cartDispatch({
				type: CART_ACTION_TYPES.UPDATE_ITEM_FROM_CART,
				payload: {
					quantity: Number(decreaseQuantityForm.value!.quantity),
					id: orderItemId,
				},
			})
		},
	})
	const removeItemFormValues = {
		[ROOT_INPUTS.subaction]: CART_FORM_REMOVE_ITEM.subaction,
		[ROOT_INPUTS.noJs]: !isHydrated.toString(),
		[ROOT_INPUTS.redirect]: `${location.pathname}`,
		[CART_INPUTS.itemId]: orderItemId?.toString(),
	}
	const [removeItemForm, removeItemFields] = useForm({
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: cartRemoveItemSchema })
		},
		shouldValidate: 'onSubmit',
		async onSubmit(e) {
			e.preventDefault()

			removeItemFetcher.submit(e.currentTarget, {
				fetcherKey: removeItemFetcherKey,
				debounceTimeout: 0,
			})

			cartDispatch({
				type: CART_ACTION_TYPES.REMOVE_ITEM_FROM_CART,
				payload: {
					id: orderItemId,
				},
			})
		},
	})

	/**
	 * Interaction
	 */
	const { t } = useTranslation(CART_I18NEXT.routeCart)
	const [isChanged, setIsChanged] = React.useState(false)

	useCartSetQuantityNotify({ setApp, setQuantityFetcher, t, name, location })
	useCartRemoveItemNotify({ setApp, removeItemFetcher, t, name, location })

	return {
		item,
		setQuantityFetcherKey,
		setQuantityFetcher,
		removeItemFetcherKey,
		removeItemFetcher,
		setQuantityFormValues,
		setQuantityForm,
		setQuantityFields,
		increaseQuantityFormValues,
		increaseQuantityForm,
		increaseQuantityFields,
		decreaseQuantityFormValues,
		decreaseQuantityForm,
		decreaseQuantityFields,
		removeItemFormValues,
		removeItemForm,
		removeItemFields,
		t,
		location,
		isHydrated,
		isChanged,
		setIsChanged,
		pending,
		actionAdd,
		actionUpdate,
		actionRemove,
		actionInit,
		errorMessage,
	}
}

export { type UseCartItemProps, useCartItem }
